import { supabase } from '../../../core/lib/supabase';
import { KnowledgeBase } from '../../../types/backendTypes';
import { WorkPillExecution, ExecutionSummary, Schema, WorkPill } from '../../../types/navigationTypes';
import { createKnowledgeBaseForExecution } from '../../knowledgebase/services/knowledgeBaseService';
import { uploadTemplateToExecutionStorage, uploadUserDocxToExecutionTemplate, BLANK_TEMPLATE_ID } from './uploadTemplateToExecutionStorage';
import { processTemplateViaOrganizationBackend } from './organizationBackendTemplateService';

export interface CreateExecutionOptions {
  /** Blank project: optional starter .docx (creates workpill_execution_templates + upload). */
  blankStarterDocx?: Blob;
}

type CatalogTemplateRow = {
  id: string;
  type?: string | null;
  content_url?: string | null;
  pdf_url?: string | null;
};

async function deleteExecutionById(executionId: string): Promise<void> {
  await supabase.from('workpill_executions').delete().eq('id', executionId);
}

/** Same rule as web validateSelectedTemplatesHaveFiles: need a file in catalog. */
function templateHasStorageFile(t: CatalogTemplateRow): boolean {
  return Boolean((t.content_url && t.content_url.trim()) || (t.pdf_url && t.pdf_url.trim()));
}

async function fetchWorkpillProvisioningPayload(workpillId: string): Promise<{
  templateRows: CatalogTemplateRow[];
  knowledgeBaseIds: string[];
}> {
  const { data, error } = await supabase
    .from('workpills')
    .select(
      `
      id,
      workpill_templates ( templates ( id, type, content_url, pdf_url ) ),
      workpill_knowledge_bases ( knowledge_base_id )
    `
    )
    .eq('id', workpillId)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('WorkPill not found');

  const d = data as unknown as {
    workpill_templates?: { templates: CatalogTemplateRow | CatalogTemplateRow[] | null }[];
    workpill_knowledge_bases?: { knowledge_base_id: string }[];
  };

  const rawTemplates = d.workpill_templates || [];
  const templateRows: CatalogTemplateRow[] = [];
  for (const r of rawTemplates) {
    const t = r.templates;
    if (!t) continue;
    const arr = Array.isArray(t) ? t : [t];
    for (const row of arr) {
      if (row?.id) templateRows.push(row);
    }
  }

  const seen = new Set<string>();
  const uniqueTemplates: CatalogTemplateRow[] = [];
  for (const t of templateRows) {
    if (!seen.has(t.id)) {
      seen.add(t.id);
      uniqueTemplates.push(t);
    }
  }

  const kbRows = d.workpill_knowledge_bases || [];
  const knowledgeBaseIds = kbRows.map((r) => r.knowledge_base_id).filter(Boolean);

  return { templateRows: uniqueTemplates, knowledgeBaseIds };
}

async function provisionExecutionTemplates(
  executionId: string,
  catalogTemplates: CatalogTemplateRow[]
): Promise<void> {
  const usable = catalogTemplates.filter(templateHasStorageFile);
  if (catalogTemplates.length > 0 && usable.length === 0) {
    throw new Error(
      'This WorkPill has templates but none have an uploaded file (content_url / pdf_url). Add files in the catalog or remove the templates.'
    );
  }
  if (usable.length === 0) return;

  const links = usable.map((t) => ({
    workpill_execution_id: executionId,
    template_id: t.id,
  }));

  const { data: inserted, error: insErr } = await supabase
    .from('workpill_execution_templates')
    .insert(links)
    .select('id, template_id');

  if (insErr) throw insErr;
  const rows = (inserted || []) as { id: string; template_id: string }[];

  for (const row of rows) {
    const { id: executionTemplateId, template_id: templateId } = row;
    try {
      await processTemplateViaOrganizationBackend(templateId, {
        execution_template_id: executionTemplateId,
        from_latest: true,
      });
    } catch (e) {
      console.warn('[ExecutionService] processTemplate failed, falling back to storage upload:', e);
      const meta = usable.find((t) => t.id === templateId);
      if (!meta?.content_url?.trim()) {
        throw new Error(
          `Template ${templateId} could not be processed by organization-backend and has no content_url for storage fallback (PDF-only templates need a successful process step).`
        );
      }
      try {
        await uploadTemplateToExecutionStorage({
          executionTemplateId,
          templateId,
          contentUrl: meta.content_url,
        });
      } catch (fallbackErr) {
        console.error('[ExecutionService] Fallback upload failed:', fallbackErr);
        throw fallbackErr;
      }
    }
  }
}

async function linkExecutionKnowledgeBases(executionId: string, knowledgeBaseIds: string[]): Promise<void> {
  if (knowledgeBaseIds.length === 0) return;
  const rows = knowledgeBaseIds.map((knowledge_base_id) => ({
    workpill_execution_id: executionId,
    knowledge_base_id,
  }));
  const { error } = await supabase.from('workpill_execution_knowledge_bases').insert(rows);
  if (error) throw error;
}

async function maybeCreateDefaultKb(userId: string, executionId: string, name: string): Promise<void> {
  try {
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('current_organization_id')
      .eq('id', userId)
      .maybeSingle();

    if (profileError || !profile?.current_organization_id) {
      console.warn('⚠️ Skipping default KB: no organization on profile', profileError);
      return;
    }

    const kbId = await createKnowledgeBaseForExecution({
      name: `Project ${name} KB`,
      description: `Automatically created KB for project: ${name}`,
      executionId,
      userId,
      organizationId: profile.current_organization_id,
      is_global: true,
      type: 'normal',
    });
    if (!kbId) {
      console.warn('⚠️ Default KB creation returned no id');
    }
  } catch (kbError) {
    console.error('⚠️ Error creating automatic KB for project:', kbError);
  }
}

function mapExecutionRow(execution: Record<string, unknown>): WorkPillExecution {
  const wpRaw = execution.workpills;
  const workpillData = Array.isArray(wpRaw) ? wpRaw[0] : wpRaw;
  const wp = workpillData as
    | { id: string; name: string; description?: string; created_at: string; created_by: string }
    | null
    | undefined;

  return {
    id: execution.id as string,
    workpill_id: (execution.workpill_id as string | null) ?? null,
    created_by: execution.created_by as string,
    state_id: execution.state_id as string,
    name: (execution.name as string) || 'Unnamed Execution',
    comments: execution.comments as string | undefined,
    last_update: execution.last_update as string,
    created_at: execution.created_at as string,
    workpill: wp
      ? {
          id: wp.id,
          name: wp.name,
          description: wp.description,
          created_at: wp.created_at,
          created_by: wp.created_by,
        }
      : undefined,
  };
}

// ==========================================
// MAIN EXECUTION SERVICE CLASS
// ==========================================

export class ExecutionService {
  static async getWorkPills(): Promise<WorkPill[]> {
    try {
      const { data: workpills, error } = await supabase.from('workpills').select('*').order('name');

      if (error) {
        console.error('❌ Error fetching workpills:', error);
        return [];
      }

      return workpills || [];
    } catch (error) {
      console.error('💥 Error in getWorkPills:', error);
      return [];
    }
  }

  /**
   * Create a project (workpill_execution): matches web create-project-wizard flow.
   * - workpillId null = blank project (no WorkPill); optional blankStarterDocx seeds one Word template row.
   * - workpillId set = link catalog templates, process via org-backend (fallback upload), link WorkPill KBs.
   */
  static async createExecution(
    userId: string,
    workpillId: string | null,
    name: string,
    options?: CreateExecutionOptions
  ): Promise<WorkPillExecution | null> {
    const executionId = crypto.randomUUID();
    const trimmedName = name.trim();

    const { data: execution, error } = await supabase
      .from('workpill_executions')
      .insert({
        id: executionId,
        created_by: userId,
        workpill_id: workpillId,
        name: trimmedName,
        last_update: new Date().toISOString(),
      })
      .select(
        `
          *,
          workpills (
            id,
            name,
            description,
            created_at,
            created_by
          )
        `
      )
      .single();

    if (error) {
      console.error('❌ Error creating execution:', error);
      return null;
    }

    try {
      if (workpillId) {
        const { templateRows, knowledgeBaseIds } = await fetchWorkpillProvisioningPayload(workpillId);
        await provisionExecutionTemplates(executionId, templateRows);
        await linkExecutionKnowledgeBases(executionId, knowledgeBaseIds);
        if (knowledgeBaseIds.length === 0) {
          await maybeCreateDefaultKb(userId, executionId, trimmedName);
        }
      } else {
        if (options?.blankStarterDocx) {
          const { data: etRow, error: etErr } = await supabase
            .from('workpill_execution_templates')
            .insert({
              workpill_execution_id: executionId,
              template_id: BLANK_TEMPLATE_ID,
            })
            .select('id')
            .single();

          if (etErr) throw etErr;
          const etId = etRow?.id as string;
          await uploadUserDocxToExecutionTemplate(etId, options.blankStarterDocx);
        }
        await maybeCreateDefaultKb(userId, executionId, trimmedName);
      }
    } catch (provisionError) {
      console.error('❌ Provisioning failed, rolling back execution:', provisionError);
      await deleteExecutionById(executionId);
      throw provisionError;
    }

    return mapExecutionRow(execution as Record<string, unknown>);
  }

  static async getUserExecutions(userId: string): Promise<WorkPillExecution[]> {
    try {
      const { data: executions, error } = await supabase
        .from('workpill_executions')
        .select(
          `
          *,
          workpills (
            id,
            name,
            description,
            created_at,
            created_by
          )
        `
        )
        .eq('created_by', userId)
        .eq('execution_type', 'project')
        .order('last_update', { ascending: false });

      if (error) {
        console.error('❌ Error fetching user executions:', error);
        return [];
      }

      if (!executions) {
        return [];
      }

      return executions.map((row) => mapExecutionRow(row as Record<string, unknown>));
    } catch (error) {
      console.error('💥 Error in getUserExecutions:', error);
      return [];
    }
  }

  static async getExecution(executionId: string): Promise<WorkPillExecution | null> {
    try {
      const { data: execution, error } = await supabase
        .from('workpill_executions')
        .select(
          `
          *,
          workpills (
            id,
            name,
            description,
            created_at,
            created_by
          )
        `
        )
        .eq('id', executionId)
        .maybeSingle();

      if (error) {
        console.error('❌ Error fetching execution:', error);
        return null;
      }

      if (!execution) {
        return null;
      }

      return mapExecutionRow(execution as Record<string, unknown>);
    } catch (error) {
      console.error('💥 Error in getExecution:', error);
      return null;
    }
  }

  static async getExecutionKnowledgeBases(executionId: string): Promise<KnowledgeBase[]> {
    try {
      const execution = await this.getExecution(executionId);
      if (!execution) {
        return [];
      }

      if (!execution.workpill_id) {
        const { data: execKbs, error: ekErr } = await supabase
          .from('workpill_execution_knowledge_bases')
          .select(
            `
            knowledge_base_id,
            knowledge_bases (
              id,
              name,
              description,
              created_at,
              created_by,
              is_global
            )
          `
          )
          .eq('workpill_execution_id', executionId);

        if (ekErr) {
          console.error('❌ Error fetching execution KBs:', ekErr);
          return [];
        }

        return (execKbs || [])
          .filter((item) => item.knowledge_bases != null)
          .map((item) => {
            const kb = item.knowledge_bases as unknown as Record<string, unknown>;
            return {
              id: kb.id as string,
              name: kb.name as string,
              description: kb.description as string | undefined,
              created_at: kb.created_at as string,
              created_by: kb.created_by as string,
              is_global: Boolean(kb.is_global),
            };
          });
      }

      const { data: workpillKbs, error: kbError } = await supabase
        .from('workpill_knowledge_bases')
        .select(
          `
          knowledge_base_id,
          knowledge_bases (
            id,
            name,
            description,
            created_at,
            created_by,
            is_global
          )
        `
        )
        .eq('workpill_id', execution.workpill_id);

      if (kbError) {
        console.error('❌ Error fetching knowledge bases:', kbError);
        return [];
      }

      const knowledgeBases: KnowledgeBase[] = (workpillKbs || [])
        .filter((item) => item.knowledge_bases != null)
        .map((item) => {
          const kb = item.knowledge_bases as unknown as Record<string, unknown>;
          return {
            id: kb.id as string,
            name: kb.name as string,
            description: kb.description as string | undefined,
            created_at: kb.created_at as string,
            created_by: kb.created_by as string,
            is_global: Boolean(kb.is_global),
          };
        });

      return knowledgeBases;
    } catch (error) {
      console.error('💥 Error in getExecutionKnowledgeBases:', error);
      return [];
    }
  }

  static async getExecutionSchemas(workpillId: string | null): Promise<Schema[]> {
    if (!workpillId) {
      return [];
    }
    try {
      const { data: workpillSchemas, error: schemaError } = await supabase
        .from('workpill_schemas')
        .select(
          `
          *,
          schemas (
            id,
            created_by,
            name,
            description,
            created_at,
            schema_fields (
              id,
              schema_id,
              name,
              type,
              description,
              created_at,
              is_extracted_automatically
            )
          )
        `
        )
        .eq('workpill_id', workpillId);

      if (schemaError) {
        console.error('❌ Error fetching schemas:', schemaError);
        return [];
      }

      const schemas: Schema[] = (workpillSchemas || [])
        .filter((item) => item.schemas != null)
        .map((item) => {
          const schema = item.schemas as Record<string, unknown> & {
            schema_fields?: Record<string, unknown>[];
          };
          return {
            id: schema.id as string,
            created_by: schema.created_by as string,
            name: schema.name as string,
            description: schema.description as string | undefined,
            created_at: schema.created_at as string,
            workpill_schema_name: item.workpill_schema_name as string | undefined,
            fields: (schema.schema_fields || []).map((field) => ({
              id: field.id as string,
              schema_id: field.schema_id as string,
              name: field.name as string,
              type: field.type as string,
              description: field.description as string | undefined,
              created_at: field.created_at as string,
              is_extracted_automatically: Boolean(field.is_extracted_automatically),
            })),
          };
        });

      return schemas;
    } catch (error) {
      console.error('💥 Error in getExecutionSchemas:', error);
      return [];
    }
  }

  static async getExecutionDocumentsCount(executionId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('workpill_execution_documents')
        .select('*', { count: 'exact', head: true })
        .eq('execution_id', executionId);

      if (error) {
        console.error('❌ Error counting documents:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('💥 Error in getExecutionDocumentsCount:', error);
      return 0;
    }
  }

  static async getExecutionStatus(executionId: string): Promise<'active' | 'completed' | 'error' | 'pending'> {
    try {
      const { data: execution, error } = await supabase
        .from('workpill_executions')
        .select('state_id')
        .eq('id', executionId)
        .maybeSingle();

      if (error) {
        console.error('❌ Error fetching execution status:', error);
        return 'pending';
      }

      if (!execution) {
        return 'pending';
      }

      const statusMap: Record<string, 'active' | 'completed' | 'error' | 'pending'> = {
        'state-1': 'active',
        'state-2': 'completed',
        'state-3': 'error',
        'state-4': 'pending',
      };

      return statusMap[execution.state_id as string] || 'pending';
    } catch (error) {
      console.error('💥 Error in getExecutionStatus:', error);
      return 'pending';
    }
  }

  static async getExecutionSummary(executionId: string): Promise<ExecutionSummary | null> {
    try {
      const execution = await this.getExecution(executionId);
      if (!execution) {
        return null;
      }

      const [knowledgeBases, schemas, documentsCount, status] = await Promise.all([
        this.getExecutionKnowledgeBases(executionId),
        this.getExecutionSchemas(execution.workpill_id),
        this.getExecutionDocumentsCount(executionId),
        this.getExecutionStatus(executionId),
      ]);

      return {
        execution,
        knowledgeBases,
        schemas,
        documentsCount,
        lastActivity: execution.last_update,
        status,
      };
    } catch (error) {
      console.error('💥 Error in getExecutionSummary:', error);
      return null;
    }
  }

  static async updateExecutionName(executionId: string, newName: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('workpill_executions')
        .update({
          name: newName,
          last_update: new Date().toISOString(),
        })
        .eq('id', executionId);

      if (error) {
        console.error('❌ Error updating execution name:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('💥 Error in updateExecutionName:', error);
      return false;
    }
  }
}
